<template>
    <div>
        <div class="flex flex-col lg:grid lg:grid-cols-10 gap-4">
            <div class="lg:col-span-7">
                <div class="p-4 border rounded-lg bg-white">
                    <planning-half-day
                    :days="$account.account.societe.open_days"
                    :heure-debut-matin="$account.account.societe.heure_ouverture ? $account.account.societe.heure_ouverture.slice(0, 5) : '08:00'"
                    heurefin-matin="12:00"
                    heure-debut-apres-midi="12:01"
                    :heure-fin-apres-midi="$account.account.societe.heure_fermeture ? $account.account.societe.heure_fermeture.slice(0, 5) : '17:00'"
                    @event-click="onEventClick"
                    @slot-selection-change="onSlotSelectionChange"
                    :multi-slots="true"
                    :readonly="step === 'chantier-form'"
                    />
                </div>
            </div>
            <div class="lg:col-span-3">
                <div class="p-4 border rounded-lg bg-white">
                    <div v-if="step == 'crenaux'">
                        <!-- Résumé en haut -->
                        <div class="grid grid-cols-3 gap-2 mb-4">
                            <div class="bg-gray-50 rounded-lg p-2 text-center">
                                <p class="text-xs text-gray-500 uppercase tracking-wide">Total</p>
                                <p class="text-xl font-medium">{{ totalHoursDisplay }}</p>
                                <p class="text-xs text-gray-400">au total</p>
                            </div>
                            <div class="bg-gray-50 rounded-lg p-2 text-center">
                                <p class="text-xs text-gray-500 uppercase tracking-wide">Jours</p>
                                <p class="text-xl font-medium">{{ totalDays }}</p>
                                <p class="text-xs text-gray-400">de chantier</p>
                            </div>
                            <div class="bg-gray-50 rounded-lg p-2 text-center">
                                <p class="text-xs text-gray-500 uppercase tracking-wide">Techs</p>
                                <p class="text-xl font-medium">{{ uniqueTechs.length }}</p>
                                <p class="text-xs text-gray-400 truncate" :title="uniqueTechs.join(', ')">{{ uniqueTechs.join(', ') }}</p>
                            </div>
                        </div>

                        <hr class="border-gray-100 mb-3">
                        <h3 class="text-xs font-medium uppercase tracking-widest text-gray-400 mb-3">Créneaux sélectionnés</h3>

                        <!-- Empty state -->
                        <div v-if="selectedSlots.length === 0" class="text-center text-sm text-gray-400 border border-dashed border-gray-200 rounded-lg py-8 px-4">
                            Aucun créneau sélectionné.<br>Cliquez sur des créneaux dans le planning.
                        </div>

                        <!-- Groupes par date -->
                        <div class="mb-3 max-h-[240px] lg:max-h-[300px] overflow-y-auto">
                            <div v-for="(slots, date) in slotsByDate" :key="date">
                                <div class="flex items-center gap-2 mb-2">
                                    <span class="text-xs font-medium bg-gray-100 border border-gray-200 rounded px-2 py-1">
                                        {{ formatDate(date) }}
                                    </span>
                                </div>

                                <div v-for="slot in slots" :key="slotKey(slot)" class="bg-white border border-gray-200 rounded-lg p-3 mb-2 hover:border-gray-300 transition-colors">
                                    <!-- Header ligne : badge + tech + durée actuelle -->
                                    <div class="flex items-center justify-between mb-2">
                                        <div class="flex items-center gap-2">
                                            <span
                                                :class="slot.slot === 'morning' ? 'bg-blue-50 text-blue-800' : 'bg-amber-50 text-amber-800'"
                                                class="text-xs font-medium px-2 py-0.5 rounded-full"
                                            >
                                                {{ slot.slot === 'morning' ? 'Matin' : 'Après-midi' }}
                                            </span>
                                            <span class="text-xs text-gray-500 truncate max-w-[80px]">{{ slot.techName }}</span>
                                        </div>
                                        <div class="flex items-center gap-1.5">
                                            <span class="text-sm font-medium tabular-nums">{{ minutesToDisplay(getSlotTime(slot)) }}</span>
                                            <span v-if="isManuallyEdited(slot)" class="w-1.5 h-1.5 rounded-full bg-blue-400 flex-shrink-0"></span>
                                        </div>
                                    </div>

                                    <!-- Slider row -->
                                    <div class="flex items-center gap-2">
                                        <input
                                            type="range"
                                            min="0"
                                            :max="slot.time"
                                            step="15"
                                            :value="getSlotTime(slot)"
                                            @input="onSliderInput(slotKey(slot), $event.target.value)"
                                            class="flex-1 h-1 rounded-full appearance-none cursor-pointer accent-orange-500"
                                            :style="sliderGradientStyle(slot)"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <button v-if="!chantierId" @click="displayStepForm" :disabled="selectedSlots.length === 0 || loadingCreateChantier" class="w-full mt-3 py-3 rounded-lg text-sm font-medium bg-orange-600 text-white hover:opacity-80 disabled:opacity-30 disabled:cursor-not-allowed transition-opacity">
                            Continuer <i class="fas fa-chevron-right ml-1"></i>
                        </button>
                        <button v-if="selectedSlots && selectedSlots.length && chantierId" @click="createChantier" :disabled="loadingCreateChantier" class="w-full mt-3 py-3 rounded-lg text-sm font-medium bg-green-600 text-white hover:opacity-80 disabled:opacity-30 disabled:cursor-not-allowed transition-opacity">
                            Valider <i class="fas fa-check ml-1"></i>
                        </button>
                    </div>
                    <div v-if="step == 'chantier-form'">
                        <div class="text-xl font-bold border-b pb-4 mb-4">Informations du chantier</div>
                        <chantier-form v-model="newChantier" />
                        <div class="mt-4">
                            <button :disabled="loadingCreateChantier" @click="createChantier" class="bg-orange-600 hover:bg-orange-700 text-white px-5 py-2.5 rounded-xl font-semibold transition-all flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed text-sm">
                                <template v-if="loadingCreateChantier">
                                    <svg class="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                </template>
                                <template v-else>
                                    <i class="fas fa-plus text-xs"></i>
                                    Créer le chantier
                                </template>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Modal view intervention -->
        <modal name="modal-view-intervention" height="auto" :scrollable="true" :adaptive="true" width="850px">
            <div class="p-8 overflow-y-auto max-h-[100vh]">
                <div class="flex items-center justify-between mb-4 pb-4 border-b">
                    <h2 class="text-xl font-bold flex items-center gap-2">
                        <i class="fas fa-folder-open text-red-600"></i>
                        Intervention
                    </h2>
                    <button @click="$modal.hide('modal-view-intervention')">
                        <i class="fas fa-times text-red-500"></i>
                    </button>
                </div>
                <div v-if="selectedInterventionId">
                    <intervention-view :intervention-id="selectedInterventionId" />
                </div>
            </div>
        </modal>
    </div>
</template>

<script>
import PlanningHalfDay from '../../Planning/Components/PlanningHalfDay.vue';
import ChantierForm from './ChantierForm.vue';

export default {
    name: "ChantierCreate",
    components: {
        PlanningHalfDay,
        ChantierForm
    },
    props: {
        chantierId: {
            type: Number,
            default: null
        }
    },
    data() {
        return {
            selectedInterventionId: null,
            selectedSlots: [],
            // Durées choisies manuellement, indexées par slotKey.
            // Si absent → valeur par défaut = slot.time (le maximum du créneau).
            manuallyEditedTimes: {},
            step: 'crenaux', // crenaux -> chantier-form

            loadingCreateChantier: false,
            newChantier: {
                client_id: null,
                adresse_id: null,
                creneaux: [], // { date, slot, techId, time (en minutes) }
            },
        }
    },
    computed: {
        slotsByDate() {
            return this.selectedSlots.reduce((groups, slot) => {
                if (!groups[slot.date]) groups[slot.date] = [];
                groups[slot.date].push(slot);
                return groups;
            }, {});
        },
        totalMinutes() {
            return this.selectedSlots.reduce((acc, s) => acc + this.getSlotTime(s), 0);
        },
        totalHoursDisplay() {
            return this.minutesToDisplay(this.totalMinutes);
        },
        uniqueDays() {
            return [...new Set(this.selectedSlots.map(s => s.date))];
        },
        totalDays() {
            return this.uniqueDays.reduce((acc, date) => {
                const slotsOnDay = this.selectedSlots.filter(s => s.date === date);
                return acc + (slotsOnDay.length >= 2 ? 1 : 0.5);
            }, 0);
        },
        uniqueTechs() {
            return [...new Set(this.selectedSlots.map(s => s.techName))];
        },
    },
    methods: {
        onEventClick(event) {
            if (!event.interventionId) return;
            this.selectedInterventionId = event.interventionId;
            this.$modal.show('modal-view-intervention');
        },
        slotKey(s) {
            return `${s.date}__${s.account_id}__${s.slot}`;
        },
        onSlotSelectionChange(newSlots) {
            // Nettoyer les temps manuels des slots désélectionnés
            const newKeys = new Set(newSlots.map(ns => this.slotKey(ns)));
            Object.keys(this.manuallyEditedTimes).forEach(k => {
                if (!newKeys.has(k)) this.$delete(this.manuallyEditedTimes, k);
            });
            this.selectedSlots = newSlots;
        },
        // Durée effective : manuelle si définie, sinon slot.time (max)
        getSlotTime(slot) {
            const key = this.slotKey(slot);
            return this.manuallyEditedTimes[key] !== undefined
                ? this.manuallyEditedTimes[key]
                : slot.time;
        },
        isManuallyEdited(slot) {
            const key = this.slotKey(slot);
            return this.manuallyEditedTimes[key] !== undefined
                && this.manuallyEditedTimes[key] !== slot.time;
        },
        onSliderInput(key, value) {
            this.$set(this.manuallyEditedTimes, key, Math.max(0, parseInt(value) || 0));
        },
        // Le gradient du slider ne peut pas être calculé dynamiquement via Tailwind,
        // un style inline est nécessaire pour la partie "remplie".
        sliderGradientStyle(slot) {
            const pct = slot.time > 0
                ? Math.round((this.getSlotTime(slot) / slot.time) * 100)
                : 0;
            return {
                background: `linear-gradient(to right, rgba(234, 88, 12, 0.6) 0%, rgba(234, 88, 12, 0.6) ${pct}%, #e5e7eb ${pct}%, #e5e7eb 100%)`
            };
        },
        formatDate(dateStr) {
            const d = new Date(dateStr + 'T00:00:00');
            return d.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' });
        },
        minutesToDisplay(min) {
            if (!min && min !== 0) return '0min';
            const h = Math.floor(min / 60), m = min % 60;
            if (h === 0) return `${m}min`;
            if (m === 0) return `${h}h`;
            return `${h}h${String(m).padStart(2, '0')}`;
        },
        displayStepForm() {
            this.newChantier = {
                client_id: null,
                adresse_id: null,
                creneaux: this.selectedSlots.map(s => ({
                    date: s.date,
                    slot: s.slot,
                    tech_id: s.account_id,
                    time: this.getSlotTime(s),
                })),
            };
            this.step = 'chantier-form';
        },
        async createChantier() {
            try {
                this.loadingCreateChantier = true;
                let chantierId = 0
                if (!this.chantierId) {
                    if (!this.newChantier.client_id || !this.newChantier.adresse_id) {
                        this.$toast.error("Veuillez sélectionner un client et une adresse.");
                        return;
                    }
                    const response = await this.$request.post('/chantiers/create', this.newChantier);
                    const chantier = response.data || response;
                    chantierId = chantier.id;
                } else {
                    chantierId = this.chantierId;
                    this.newChantier.creneaux = this.selectedSlots.map(s => ({
                        date: s.date,
                        slot: s.slot,
                        tech_id: s.account_id,
                        time: this.getSlotTime(s),
                    }));
                }


                let creneaux = this.newChantier.creneaux.map(c => ({
                    chantier_id: chantierId,
                    date: c.date,
                    slot: c.slot,
                    tech_id: c.tech_id,
                    time: c.time,
                }));
                await this.$request.post('/chantiers/reserver-creneaux', { 
                    'creneaux': JSON.stringify(creneaux)
                });
                this.newChantier = { client_id: null, adresse_id: null };
                if (this.chantierId) {
                    this.$emit('slots-created', chantierId);
                } else {
                    this.$emit('chantier-created', 1);
                }
            } catch (error) {
                console.error('Erreur lors de la création du chantier:', error);
                this.$toast.error("Une erreur est survenue lors de la création du chantier.");
            } finally {
                this.loadingCreateChantier = false;
            }
        },
    }
}
</script>